import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useLocation, useNavigate } from '@/lib/router-dom';
import { ReactComponent as IconChevronDown } from "@/assets/svgs/chevron-down.svg";
import { ReactComponent as IconNoMessage } from "@/assets/svgs/no-message.svg";
import { ReactComponent as IconPrevious } from '@/assets/svgs/transactions-back-icon.svg';
import { ReactComponent as IconUpdate } from '@/assets/svgs/upload-icon.svg';
import { ReactComponent as IconProfileChevronDown } from '@/assets/svgs/chevron-down-small.svg';
import Breadcrumbs from "@/componentsDS/Breadcrumbs/index.jsx";
import Search from "@/componentsDS/Search/index";
import api from "@/services/api/master-data-management.js";
import moment from "moment";
import rem from "@/utils/rem";
import { features as featureConstant } from "@/utils/feature-verifier";
import { useFeature } from "@/hooks/use-feature/index";
import { useBuildex } from "@/hooks/use-buildex";
import usePage from "@/hooks/use-page";
import useDataPage from "@/hooks/use-data-page/index";
import useMasterDataManagement from "@/hooks/use-master-data-management/index";
import getPageLabel from "@/hooks/use-page-details/index";
import useToast from "@/hooks/use-toast";
import MultiLevelDropDown from "@/components/MultiLevelMenuDropDown/index.js";
import Toast from "@/components/Toast/index";
import toastType from "@/constants/toast-type/index";

const MasterDataManagementDetailsPage = () => {
	/*=================================================1
		General
	/================================================*/
	const isBuildex = useBuildex();
	const isSupersetDashboard = useFeature(featureConstant.DASHBOARD);
	const isHomePage = !(isBuildex && isSupersetDashboard);
	const [pageTitle] = useState("Master Data Management");
	const { currentState } = useLocation();

	const [currentDetailsPage, setCurrentDetailsPage] = useState(1);
	const [pageInputValue, setPageInputValue] = useState("1");
	const [detailsPageLoading, setDetailsPageLoading] = useState(false);

	const [searchText, setSearchText] = useState("");
	const [displayedSearchText, setDisplayedSearchText] = useState("");
	const [filter, setFilter] = useState({});
	const [searchTimeout, setSearchTimeout] = useState(null);

	const { goTo } = useNavigate();
	const pageLimit = 10;

	const { breadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs: [
			{
				label: "",
				link: `/`,
				isHomePage: isHomePage,
			},
			{
				label: "Master Data Management",
				link: `/master-data-management`,
			},
			{
				label: `${currentState?.item?.name}`,
				link: ``,
			},
		],
	});

	/*=================================================
		Error Modal
	/================================================*/
	const { toastList, addToastMessage } = useToast();
	const [error, setError] = useState();

	useEffect(() => {
		addToastMessage(error, toastType.error);
	}, [error]);

	/*=================================================
		Master Data Management
	/================================================*/
	const {
		fetchMasterDataUploadDetails,
		masterDataListDetails,
		metaData,
		masterDataLoading,
	} = useMasterDataManagement();

	/*=================================================
        Sort
    /================================================*/
	const { sortConfig, handleSort } = useDataPage();

	function getClassName(name) {
		if (!sortConfig) {
			return;
		}
		return sortConfig.key === name ? sortConfig.direction === true ? "is-asc" : "is-desc" : "";
	}

	/*=================================================
        Pagination Events
    /================================================*/
	function goToPrevPage() {
		if (currentDetailsPage === 1) return;
		const newPage = currentDetailsPage - 1;
		setCurrentDetailsPage(newPage);
		setPageInputValue(newPage.toString());
	}

	function goToNextPage() {
		if (currentDetailsPage === metaData?.totalPages) return;
		const newPage = currentDetailsPage + 1;
		setCurrentDetailsPage(newPage);
		setPageInputValue(newPage.toString());
	}

	function handlePageInputChange(e) {
		const inputValue = e.target.value;
		setPageInputValue(inputValue);
		
		// Only update page for valid numbers within range
		if (inputValue !== "") {
			const pageNumber = Number(inputValue);
			const totalPages = metaData?.totalPages || 1;
			if (pageNumber >= 1 && pageNumber <= totalPages) {
				setCurrentDetailsPage(pageNumber);
			}
		}
	}

	function handlePageInputBlur() {
		const pageNumber = Number(pageInputValue);
		const totalPages = metaData?.totalPages || 1;
		
		if (pageInputValue === "" || pageNumber < 1) {
			// Empty or invalid, go to page 1
			setCurrentDetailsPage(1);
			setPageInputValue("1");
		} else if (pageNumber > totalPages) {
			// More than total pages, go to last page
			setCurrentDetailsPage(totalPages);
			setPageInputValue(totalPages.toString());
		}
	}

	function handlePageInputKeyDown(e) {
		if (e.key === 'Enter') {
			e.target.blur();
		}
	}

	/*=================================================
        Search
    /================================================*/
	const handleInputSearch = (value) => {
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}
		setSearchTimeout(
			setTimeout(() => {
				setCurrentDetailsPage(1);
				setSearchText(value);
				setFilter({
					search: value,
				});
			}, 500)
		);
	};

	/*=================================================
		Refetch Data Filters
	/================================================*/
	const refetchDataTable = (page = currentDetailsPage) => {
		const params = {
			page: page,
			limit: 10,
			sortOrder: sortConfig.direction === true ? "ASC" : "DESC",
			sortBy: sortConfig.key,
			searchBy: searchText,
		};
		setCurrentDetailsPage(page);
		fetchMasterDataUploadDetails(currentState?.item?.id, params);
	};

	useEffect(() => {
		refetchDataTable();
		setPageInputValue(currentDetailsPage.toString()); // Sync input with page changes
	}, [currentState, currentDetailsPage, filter, sortConfig]);

	/*=================================================
		View Master Data Details Navigation
	/================================================*/
	function backButton() {
		return goTo(`/master-data-management`);
	}
	function onViewUpdateBtnClick() {
		return goTo(`/master-data-management/update/${currentState?.item?.id}`, { dataElementName: currentState?.item.name, schema: currentState?.item.jsonSchema, entityId: currentState?.item?.id, uniqueKeysOfSchema: currentState?.item?.uniqueKeysOfSchema });
	}

	/*=================================================
		Download CSV Template and Master Data
	/================================================*/
	function menuItemsClickFunction(e, type) {
		e.stopPropagation();
		if (type === "download") {
			downloadCSVTemplateMasterData("download");
		} else if (type === "get-template") {
			downloadCSVTemplateMasterData("get-template");
		}
	}

	async function downloadCSVTemplateMasterData(type) {
		setDetailsPageLoading(true);
		const [error, result] = await api.downloadCSVTemplateMasterData(currentState?.item?.id, type);
		setDetailsPageLoading(false);
		if (error) {
			return setError(
				error?.error?.message || "Failed to download file(s)"
			);
		}
		const link = document.createElement("a");
		const href = `data:text/csv;charset=utf-8,%EF%BB%BF${encodeURIComponent(
			result?.response
		)}`;
		link.href = href;
		link.download = `${result?.filename ?? type + ".csv"}`;
		link.click();
	}

	const headers = {};
	const entitySchema = currentState?.item?.jsonSchema?.items?.properties
	Object.keys(entitySchema).forEach((key) => {
		if (entitySchema[key].title) {
			headers[key] = entitySchema[key].title;
		}
		else {
			headers[key] = formatHeader(key);
		}
	});

	// Helper function to prettify headers
	function formatHeader(key) {
		return key
			.replace(/([A-Z])/g, " $1") // add space before capital letters
			.replace(/_/g, " ") // replace underscores
			.replace(/\b\w/g, (c) => c.toUpperCase()); // capitalize words
	}

	// 🔹 Helper: Format values (detect dates)
	function formatValue(value) {
		if (!value) return "-";

		// Check if value looks like a date
		if (moment(value, moment.ISO_8601, true).isValid()) {
			return moment(value).format("DD MMM YYYY"); // e.g., 12 Mar 2025
		}

		return value;
	}

	/*=================================================
		List of Master Data
	/================================================*/
	const list = masterDataListDetails?.map((item, index) => {
		return (
			<div className="master-data-table__table__body__row" key={index}>
				<div className="data-table__table__body__row-cursor">
					{Object.keys(headers).map((headerKey) => (
						<div
							key={headerKey}
							className="data-table__table__body__content etr-issuance-data-table__table__body__content-master-data-name master-data-table__table__head-body__content-row"
							data-testid={headerKey}
						>
							<div>{formatValue(item?.[headerKey])}</div>
						</div>
					))}
				</div>
			</div>
		);
	});

	const renderPaginationInput = () => (
		<input
			className={"etr-maker-checker__data-table__table__pagination__page"}
			type="number"
			value={pageInputValue}
			onChange={handlePageInputChange}
			onKeyDown={handlePageInputKeyDown}
			onBlur={handlePageInputBlur}
			min="1"
		/>
	);

	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />
			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__back__text-icon" data-testid="master-data-back-icon" role="none" onClick={backButton}><IconPrevious /></div>
					<div className="title-header__title">
						<span>{currentState?.item?.name}</span>
						<br />
						<div className="title-header__subtitle-master-data">{currentState?.item?.category}</div>
						<div className="title-header__subtitle-master-data title-header__subtitle-master-data__updated">
							<div>{`Last updated on: ${moment(currentState?.item?.modified).format("DD MMMM YYYY")} at ${moment(currentState?.item?.modified).format("h.mmA")} `}</div> 
							<div className="title-header__subtitle-master-data__updated__dot">.</div>
							<div>{`Updated by: ${currentState?.item?.modifiedBy}`}</div>
						</div>
					</div>
				</div>
				<div className="title-header__right">
					<div className="title-header__right__update-data-text">
						<MultiLevelDropDown
							button={() => (
								<div className="title-header__right__update-data-text__button">
									<span>Download</span>
									<IconProfileChevronDown />
								</div>
							)}
							menuList={[
								{
									label: "Download Master Data",
									onClick: (e) => menuItemsClickFunction(e, "download")
								},
								{
									label: "Download CSV Template",
									onClick: (e) => menuItemsClickFunction(e, "get-template")
								}
							]}
						/>
					</div>
					<div className="title-header__right__update-data" data-testid="master-data-update-data" onClick={onViewUpdateBtnClick} role="none">
						<IconUpdate />
						<span>Update Data</span>
					</div>
				</div>
			</div>

			<div className="etr-issuance-record-list-flex">
				<div className="data-table__table__head__row__search">
					<div
						style={{ maxWidth: rem(550), paddingBottom: "1.5rem" }}
					>
						<div style={{ width: "350px" }}>
							<Search
								value={displayedSearchText}
								placeholder={"Search"}
								onChange={(e) => {
									setDisplayedSearchText(e.target.value);
									handleInputSearch(e.target.value);
								}}
								onCancel={() => {
									setDisplayedSearchText("");
									handleInputSearch("");
								}}
							/>
						</div>
					</div>
				</div>
			</div>
			<div className="etr-issuance-record-data">
				<div>
					<div className="data-table__table__body">
						<div>
							<div className="master-data-table__table__head__row">
								<div className="data-table__table__body__row-cursor">
									{Object.keys(headers).map((headerKey) => (
										<div
											key={headerKey}
											className={`data-table__table__head__title data-table__table__head__title__sort-order etr-issuance-data-table__table__head__title-master-data-name master-data-table__table__head-body__content-row link ${getClassName?.(
												headerKey
											)}`}
											onClick={() => handleSort?.(headerKey)}
											onKeyDown={() => handleSort?.(headerKey)}
											id={headerKey}
											role="none"
										>
											<div>{headers[headerKey]}</div>
											<div className="etr-maker-checker-btn-group">
												<button className="data-table__table__head__icon__sort-order-up">
													<IconChevronDown />
												</button>
												<button className="data-table__table__head__icon__sort-order-down">
													<IconChevronDown />
												</button>
											</div>
										</div>
									))}
								</div>
							</div>
							{masterDataListDetails?.length > 0 ? (
								list
							) : (
								<div className="etr-issuance-no-message-record">
									<div className="data-table__empty__icon">
										<IconNoMessage />
										<div>No Master Data Uploaded yet.</div>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
				{masterDataListDetails?.length > 0 && (
					<div className="data-table__table__body-footer">
						<div className="data-table__table__footer">
							<div className="data-table__table__footer__row">
								<div className="data-table__table__footer__row__left">
									<div className="data-table__table__meta">
										Showing{" "}
										{getPageLabel(
											metaData?.total,
											pageLimit,
											currentDetailsPage - 1
										)}{" "}
										of {metaData?.total || 0} entries
									</div>
								</div>
								<div className="data-table__table__footer__row__right">
									<div className="data-table__table__pagination">
										{renderPaginationInput()}
										<span className="data-table__table__pagination__page__meta">
											{" "}
											of {metaData?.totalPages ||
												1} pages{" "}
										</span>
										<button
											data-testid="pagination-prev"
											className={
												"etr-maker-checker__data-table__table__pagination__prev"
											}
											onClick={() => goToPrevPage()}
										>
											<IconChevronDown />
										</button>
										<button
											data-testid="pagination-next"
											className={
												"etr-maker-checker__data-table__table__pagination__next"
											}
											onClick={() => goToNextPage()}
										>
											<IconChevronDown />
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>

			{(masterDataLoading || detailsPageLoading) &&
				ReactDOM.createPortal(
					<div className="modal white">
						<div className="loading">
							<img src="/loading.gif" alt="loader" />
						</div>
					</div>,
					document.body
				)}

			<Toast autoDelete={true} toastList={toastList} />
		</>
	);
};

export default MasterDataManagementDetailsPage;
